import { currencyFormatter } from '@/helpers/currencyFormatter';
import { digitsOnlyBeforeInput, formatCentsInput } from '@/helpers/inputHelpers';
import { useSendDirectPaymentLinkMutation } from '@/services/accounts';
import { Box, Button, Modal, TextareaInput, TextInput, toast } from '@palmetto/palmetto-components';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { ApplicantType, FinancingDataResponse } from 'types';

interface SendPaymentLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  account: any;
  financingData: FinancingDataResponse | undefined;
}

const DESCRIPTION_MAX_LENGTH = 200;
const DEFAULT_EXPIRATION_DAYS = 1;
const MIN_EXPIRATION_DAYS = 1;
const MAX_EXPIRATION_DAYS = 14;
const HOURS_PER_DAY = 24;
const DEFAULT_EXPIRATION_DESCRIPTION = `${DEFAULT_EXPIRATION_DAYS} ${DEFAULT_EXPIRATION_DAYS === 1 ? 'day' : 'days'}`;
const PILL_STYLE = { borderRadius: '999px' };
const INFO_GRID_STYLE = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr',
  gap: 'var(--size-spacing-xl) var(--size-spacing-xl)',
};

function formatCurrency(value: string | undefined): string {
  if (!value) return '$0.00';
  return currencyFormatter.format(parseFloat(value));
}

export function SendPaymentLinkModal({ isOpen, onClose, account, financingData }: SendPaymentLinkModalProps) {
  const { id: accountId = '' } = useParams<{ id: string }>();
  const [sendDirectPaymentLink, { isLoading, reset }] = useSendDirectPaymentLinkMutation();
  const [selectedApplicantType, setSelectedApplicantType] = useState<'primary' | 'secondary'>('primary');
  const [deliveryChoice, setDeliveryChoice] = useState<'email' | 'sms' | 'both'>('email');
  const [description, setDescription] = useState('');
  const [minAllowedPayment, setMinAllowedPayment] = useState<string>('');
  const [expirationDays, setExpirationDays] = useState<string>('');

  const applicants = account?.applicants ?? [];
  const primaryApplicant = applicants.find((a: any) => a.type === ApplicantType.primary);
  const secondaryApplicant = applicants.find((a: any) => a.type === ApplicantType.secondary);
  const selectedApplicant = selectedApplicantType === 'primary' ? primaryApplicant : secondaryApplicant;
  const fullName = selectedApplicant ? `${selectedApplicant.firstName} ${selectedApplicant.lastName}` : '';
  const isPastDue = parseFloat(financingData?.amountPastDue ?? '0') > 0;
  const hasPhone = Boolean(selectedApplicant?.phoneNumber);
  const hasEmail = Boolean(selectedApplicant?.email);
  const missingContactForChoice =
    (deliveryChoice === 'sms' && !hasPhone) ||
    (deliveryChoice === 'email' && !hasEmail) ||
    (deliveryChoice === 'both' && (!hasPhone || !hasEmail));
  const missingContactMessage =
    deliveryChoice === 'sms' && !hasPhone
      ? 'No phone number on file for this applicant.'
      : deliveryChoice === 'email' && !hasEmail
        ? 'No email on file for this applicant.'
        : deliveryChoice === 'both' && !hasPhone
          ? 'No phone number on file — add one to send via both channels.'
          : deliveryChoice === 'both' && !hasEmail
            ? 'No email on file — add one to send via both channels.'
            : null;

  const parsedMinAllowedPayment = minAllowedPayment.trim() === '' ? null : parseFloat(minAllowedPayment);
  const minAllowedPaymentError =
    parsedMinAllowedPayment !== null && (Number.isNaN(parsedMinAllowedPayment) || parsedMinAllowedPayment < 1)
      ? 'Minimum must be at least $1.00'
      : null;

  const trimmedExpiration = expirationDays.trim();
  const parsedExpirationDays = trimmedExpiration === '' ? null : Number(trimmedExpiration);
  const expirationHours =
    parsedExpirationDays !== null && Number.isInteger(parsedExpirationDays) && parsedExpirationDays > 0
      ? parsedExpirationDays * HOURS_PER_DAY
      : null;
  const expirationError = (() => {
    if (parsedExpirationDays === null) return null;
    if (!Number.isInteger(parsedExpirationDays) || parsedExpirationDays < MIN_EXPIRATION_DAYS) {
      return `Minimum is ${MIN_EXPIRATION_DAYS} day.`;
    }
    if (parsedExpirationDays > MAX_EXPIRATION_DAYS) return `Maximum is ${MAX_EXPIRATION_DAYS} days.`;
    return null;
  })();

  useEffect(() => {
    if (isOpen) {
      setSelectedApplicantType('primary');
      setDeliveryChoice('email');
      setDescription('');
      setMinAllowedPayment('');
      setExpirationDays('');
      reset();
    }
    // reset is stable but omitting it avoids re-running if RTK ever changes its reference mid-flight
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setDeliveryChoice('email');
    setDescription('');
    setMinAllowedPayment('');
    setExpirationDays('');
    reset();
    onClose();
  }, [onClose, reset]);

  const handleSelectPrimary = useCallback(() => setSelectedApplicantType('primary'), []);
  const handleSelectSecondary = useCallback(() => setSelectedApplicantType('secondary'), []);
  const handleSelectEmail = useCallback(() => setDeliveryChoice('email'), []);
  const handleSelectSms = useCallback(() => setDeliveryChoice('sms'), []);
  const handleSelectBoth = useCallback(() => setDeliveryChoice('both'), []);

  const handleDescriptionChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(event.target.value.slice(0, DESCRIPTION_MAX_LENGTH));
  }, []);

  const handleMinAllowedPaymentChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setMinAllowedPayment(formatCentsInput(event.target.value));
  }, []);

  const handleExpirationDaysChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value.replace(/[^0-9]/g, '');
    setExpirationDays(next);
  }, []);

  const handleSend = useCallback(async () => {
    const deliveryMethods: ('email' | 'sms')[] = deliveryChoice === 'both' ? ['email', 'sms'] : [deliveryChoice];
    try {
      await sendDirectPaymentLink({
        accountId,
        applicantType: selectedApplicantType,
        deliveryMethods,
        ...(description ? { description } : {}),
        ...(parsedMinAllowedPayment !== null &&
        !Number.isNaN(parsedMinAllowedPayment) &&
        parsedMinAllowedPayment >= 1
          ? { minAllowedPayment: parsedMinAllowedPayment }
          : {}),
        ...(expirationHours !== null && expirationError === null ? { expirationHours } : {}),
      }).unwrap();
      onClose();
    } catch {
      toast.error('Failed to send payment link');
    }
  }, [
    sendDirectPaymentLink,
    accountId,
    selectedApplicantType,
    deliveryChoice,
    description,
    parsedMinAllowedPayment,
    expirationHours,
    expirationError,
    onClose,
  ]);

  return (
    <Modal ariaLabelledBy="send-payment-link-title" isOpen={isOpen} onDismiss={handleClose} maxWidth="700px">
      <Modal.Header id="send-payment-link-title" title="Send a direct payment link" onDismiss={handleClose} />
      <Modal.Body childGap="lg">
        {/* Applicant pills */}
        <Box direction="row" childGap="sm">
          {primaryApplicant && (
            <Button
              variant={selectedApplicantType === 'primary' ? 'primary' : 'tertiary'}
              tone="neutral"
              size="sm"
              type="button"
              onClick={handleSelectPrimary}
              style={PILL_STYLE}
            >
              {`${primaryApplicant.firstName} ${primaryApplicant.lastName}`}
            </Button>
          )}
          {secondaryApplicant && (
            <Button
              variant={selectedApplicantType === 'secondary' ? 'primary' : 'tertiary'}
              tone="neutral"
              size="sm"
              type="button"
              onClick={handleSelectSecondary}
              style={PILL_STYLE}
            >
              {`${secondaryApplicant.firstName} ${secondaryApplicant.lastName}`}
            </Button>
          )}
        </Box>

        {/* Customer info card */}
        <Box background="secondary" radius="md" padding="md" childGap="md">
          <Box childGap="2xs">
            <Box as="p" color="body-secondary" fontSize="sm">
              Customer
            </Box>
            <Box as="p" fontWeight="bold" fontSize="xl">
              {fullName}
            </Box>
          </Box>
          <Box style={INFO_GRID_STYLE}>
            <Box childGap="2xs">
              <Box as="p" color="body-secondary" fontSize="sm">
                Total Amount Due
              </Box>
              <Box as="p" fontWeight="bold" fontSize="md">
                {formatCurrency(financingData?.totalDue)}
              </Box>
            </Box>
            <Box childGap="2xs">
              <Box as="p" color="body-secondary" fontSize="sm">
                Amount Past Due
              </Box>
              <Box as="p" fontWeight="bold" fontSize="md" color={isPastDue ? 'danger' : undefined}>
                {formatCurrency(financingData?.amountPastDue)}
              </Box>
            </Box>
            <Box />
            <Box childGap="2xs">
              <Box as="p" color="body-secondary" fontSize="sm">
                Mailing address
              </Box>
              <Box as="p" fontSize="sm">
                {selectedApplicant?.address?.address1}
              </Box>
              <Box as="p" fontSize="sm">
                {selectedApplicant?.address?.city}, {selectedApplicant?.address?.state}{' '}
                {selectedApplicant?.address?.zip}
              </Box>
            </Box>
            <Box childGap="2xs">
              <Box as="p" color="body-secondary" fontSize="sm">
                Email
              </Box>
              <Box as="p" fontSize="sm">
                {selectedApplicant?.email}
              </Box>
            </Box>
            <Box childGap="2xs">
              <Box as="p" color="body-secondary" fontSize="sm">
                Phone
              </Box>
              <Box as="p" fontSize="sm">
                {selectedApplicant?.phoneNumber}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Delivery method */}
        <Box childGap="sm">
          <Box as="p" fontSize="sm" fontWeight="medium">
            Link Delivery Method
          </Box>
          <Box direction="row" childGap="sm">
            <Button
              variant={deliveryChoice === 'email' ? 'primary' : 'tertiary'}
              tone="neutral"
              size="sm"
              type="button"
              onClick={handleSelectEmail}
              style={PILL_STYLE}
            >
              Email
            </Button>
            <Button
              variant={deliveryChoice === 'sms' ? 'primary' : 'tertiary'}
              tone="neutral"
              size="sm"
              type="button"
              onClick={handleSelectSms}
              style={PILL_STYLE}
            >
              SMS
            </Button>
            <Button
              variant={deliveryChoice === 'both' ? 'primary' : 'tertiary'}
              tone="neutral"
              size="sm"
              type="button"
              onClick={handleSelectBoth}
              style={PILL_STYLE}
            >
              Email & SMS
            </Button>
          </Box>
          {missingContactMessage && (
            <Box as="p" fontSize="xs" color="danger">
              {missingContactMessage}
            </Box>
          )}
        </Box>

        {/* Minimum payment + Expiration + Description */}
        <Box childGap="sm">
          <Box direction="row" childGap="lg" alignItems="flex-start">
            <Box childGap="sm">
              <Box as="p" fontSize="sm" fontWeight="medium">
                Minimum payment (Optional)
              </Box>
              <Box width="180px">
                <TextInput
                  id="payment-link-min-allowed-payment"
                  label="Minimum payment (Optional)"
                  hideLabel
                  value={minAllowedPayment}
                  onChange={handleMinAllowedPaymentChange}
                  placeholder="1.00"
                  prefix="$"
                  size="sm"
                  inputProps={{ inputMode: 'numeric', onBeforeInput: digitsOnlyBeforeInput }}
                  error={minAllowedPaymentError ?? undefined}
                />
              </Box>
            </Box>

            <Box childGap="sm">
              <Box as="p" fontSize="sm" fontWeight="medium">
                Expiration in days (Optional)
              </Box>
              <Box width="180px">
                <TextInput
                  id="payment-link-expiration-days"
                  label="Expiration in days"
                  hideLabel
                  value={expirationDays}
                  onChange={handleExpirationDaysChange}
                  placeholder={String(DEFAULT_EXPIRATION_DAYS)}
                  suffix="days"
                  size="sm"
                  inputProps={{ inputMode: 'numeric', onBeforeInput: digitsOnlyBeforeInput }}
                  error={expirationError ?? undefined}
                />
              </Box>
              <Box as="p" fontSize="xs" color="body-secondary">
                {`Defaults to ${DEFAULT_EXPIRATION_DESCRIPTION} if blank`}
              </Box>
            </Box>
          </Box>

          <Box childGap="sm">
            <Box as="p" fontSize="sm" fontWeight="medium">
              Description (Optional)
            </Box>
            <Box childGap="xs">
              <TextareaInput
                id="payment-link-description"
                label=""
                value={description}
                onChange={handleDescriptionChange}
                rows={3}
              />
              <Box as="p" fontSize="xs" color="body-secondary" textAlign="right">
                {description.length}/{DESCRIPTION_MAX_LENGTH}
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal.Body>

      <Modal.Footer>
        <Box direction="row" justifyContent="space-between" alignItems="center" width="100">
          <Box as="p" fontSize="xs" color="body-secondary">
            {(() => {
              if (expirationError !== null || expirationHours === null) {
                return `Link will expire ${DEFAULT_EXPIRATION_DESCRIPTION} after creation`;
              }
              const days = expirationHours / HOURS_PER_DAY;
              return `Link will expire ${days} ${days === 1 ? 'day' : 'days'} after creation`;
            })()}
          </Box>
          <Box direction="row" childGap="sm">
            <Button
              variant="secondary"
              tone="neutral"
              size="md"
              type="button"
              onClick={handleClose}
              isDisabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="md"
              type="button"
              iconPrefix="link"
              onClick={handleSend}
              isLoading={isLoading}
              isDisabled={
                isLoading || minAllowedPaymentError !== null || expirationError !== null || missingContactForChoice
              }
            >
              Send payment link
            </Button>
          </Box>
        </Box>
      </Modal.Footer>
    </Modal>
  );
}
